import React, { useContext } from 'react';

import { RouteContext } from 'src/context';
import history from 'src/history';
import { useAppSelector } from 'src/hooks/useStore';
import { isNavItem } from 'src/legacy/components/Sidebar/SidebarNav';
import { useSidebarItems } from 'src/legacy/components/Sidebar/useSidebarItems';

interface NavigableItem {
  path: string;
  isSelected: (pathname: string) => boolean;
}

/**
 * Hook that enables Option+Up/Down keyboard navigation for the sidebar.
 * Only active for internal users (not clients).
 *
 * - Option+ArrowUp: Navigate to previous sidebar item
 * - Option+ArrowDown: Navigate to next sidebar item
 * - Wraps around at the ends of the list
 */
export const useSidebarNavigationShortcut = () => {
  const isClient = useAppSelector((state) => state.user.isClient);
  const { pathname } = useContext(RouteContext);
  const mainSidebarSections = useSidebarItems('main');

  // Flatten accessible nav items in visual order (top to bottom)
  // When an item has subItems, include the subItems in order (not the parent)
  const navigableItems = React.useMemo((): NavigableItem[] => {
    const items: NavigableItem[] = [];

    for (const section of mainSidebarSections) {
      if (!section.canAccess) continue;

      for (const navItem of section.navItems) {
        if (!navItem.canAccess) continue;

        if (navItem.subItems && navItem.subItems.length > 0) {
          // For items with subItems, add each accessible subItem
          for (const subItem of navItem.subItems) {
            if (subItem.canAccess && isNavItem(subItem)) {
              items.push({
                path: subItem.path,
                isSelected: (p: string) =>
                  subItem.selected || p.includes(subItem.path),
              });
            }
          }
        } else {
          // Regular item without subItems
          items.push({
            path: navItem.path,
            isSelected: (p: string) => navItem.selected || p === navItem.path,
          });
        }
      }
    }

    return items;
  }, [mainSidebarSections]);

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      // Only for internal users
      if (isClient) return;
      // Require Option/Alt key
      if (!event.altKey) return;

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();

        // Find current index based on selection logic
        let currentIndex = navigableItems.findIndex((item) =>
          item.isSelected(pathname),
        );

        // If no item is selected, start from the beginning (for ArrowDown) or end (for ArrowUp)
        if (currentIndex === -1) {
          currentIndex = event.key === 'ArrowDown' ? -1 : navigableItems.length;
        }

        let targetIndex: number;
        if (event.key === 'ArrowUp') {
          targetIndex =
            currentIndex <= 0 ? navigableItems.length - 1 : currentIndex - 1;
        } else {
          targetIndex =
            currentIndex >= navigableItems.length - 1 ? 0 : currentIndex + 1;
        }

        const targetItem = navigableItems[targetIndex];
        if (targetItem) {
          history.push(targetItem.path);
        }
      }
    },
    [isClient, navigableItems, pathname],
  );

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown, false);
    return () => window.removeEventListener('keydown', handleKeyDown, false);
  }, [handleKeyDown]);
};
